import * as React from "react";
import Icon from "../../Icon";
import { motion } from "framer-motion";
import AddToCalendar from "@culturehq/add-to-calendar";
import { IParts } from "@src/components/Parts";
import DialogModal from "../../Dialog";
import LiveSchedules from "../../LiveSchedules";
import { GoogleMap, useLoadScript, MarkerF } from "@react-google-maps/api";
import Button from "@src/components/Button";
import formatDateWithOrdinal, {
  formatEventTimeWithTimeZoneLabel,
} from "@src/utils/dateFormatter";
import Image from "next/image";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import { useEventParts } from "@src/hooks/useEventParts";

import defaultPartsImage from "../../../../public/images/image20.jpeg";
import bgImg from "../../../../public/images/t19bg.png";

const Parts: React.FC<IParts> = ({
  eventId,
  parts,
  eventPartsData,
  liveSchedules,
  partsImageData,
  sectionImage,
  eventTimezone,
  isInvitedToEvent,
}) => {
  const [openMap, setOpenMap] = React.useState<boolean>(false);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "",
    libraries: ["places"],
  });

  const onLoadMarker = (marker: any) => {
    // console.log("Marker", marker?.position?.lat);
  };

  const handleViewDirections = ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => {
    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving`;
    window.open(directionsUrl, "_blank");
  };

  const handleFindHotelsNearby = ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => {
    const hotelsUrl = `https://www.google.com/maps/search/hotels/@${latitude},${longitude},15z`;
    window.open(hotelsUrl, "_blank");
  };

  const { parts: derivedParts } = useEventParts({
    eventPartsData,
    liveSchedules,
    eventTimezone,
    isInvitedToEvent,
  });
  const visibleParts = parts ?? derivedParts;

  const addOneDay = (dateTime: string) => {
    var date = new Date(dateTime);
    date.setHours(date.getHours() + 24);
    var isoDate = date.toISOString();
    return isoDate;
  };

  return visibleParts.length > 0 ? (
    <div className="w-full h-[100vh] flex flex-col lg:flex-row">
      {/* Content Section - First on mobile, second on desktop */}
      <div className="flex-1 h-1/2 lg:h-full relative order-1 lg:order-2">
        {/* Background image */}
        <div className="absolute inset-0">
          <Image
            src={bgImg}
            alt="background"
            layout="fill"
            className="object-cover object-center"
            onError={(e) => console.log("Background image failed to load:", e)}
            onLoad={() => console.log("Background image loaded successfully")}
          />
        </div>

        {/* Content overlay */}
        <div className="relative z-10 h-full">
          {visibleParts.length === 0 ? (
            <></>
          ) : (
            <motion.div
              initial={{ opacity: 1 }}
              whileInView={{
                opacity: 1,
                transition: {
                  delayChildren: 0.5,
                  staggerChildren: 0.5,
                },
              }}
              className={`py-8 lg:py-[10rem] px-6 lg:px-10 h-full flex flex-col justify-center min-h-0`}
            >
              <h3 className="font-ntFab font-semibold lg:font-normal text-xl sm:text-2xl lg:text-6xl text-center text-[#FCE2B0] mb-8 lg:mb-20">
                The Event Schedule
              </h3>
              <div className="flex flex-col items-center justify-start w-full overflow-y-auto max-h-[60vh] lg:max-h-none lg:overflow-visible [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                {visibleParts.map((item, idx) => (
                  <motion.div
                    initial={{ opacity: 0, y: -15 }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                      transition: {
                        delay: 0.2,
                        duration: 1,
                        ease: [0.79, 0.14, 0.15, 0.86],
                      },
                    }}
                    key={`home action ${idx}`}
                    className={`flex flex-col justify-center items-center gap-y-2 lg:gap-y-4 w-full max-w-6xl mx-auto border-b ${
                      idx === 0 ? `border-t` : ``
                    } border-y-[#FCE2B0] py-4 lg:py-8`}
                  >
                    <h1 className="w-full text-center font-eavesSmallCaps text-[#FCE2B0] text-sm sm:text-base lg:text-2xl break-words px-2">
                      {item.name}
                    </h1>
                    <p className="w-full text-center text-[#FCE2B0] font-ebGaramond text-xs sm:text-sm lg:text-base break-words">
                      {formatDateWithOrdinal(
                        item.time,
                        item.eventPartTimezone ?? eventTimezone
                      )}
                    </p>
                    <p className="w-full text-center font-ebGaramond text-[#FCE2B0] text-xs sm:text-sm lg:text-base">
                      {formatEventTimeWithTimeZoneLabel(
                        item.time,
                        item.eventPartTimezone ?? eventTimezone
                      )}
                    </p>
                    {item.showAddress ? (
                      <p className="w-full text-center font-ebGaramond text-[#FCE2B0] text-xs sm:text-sm lg:text-base truncate px-2">
                        {item?.address}
                      </p>
                    ) : (
                      <></>
                    )}
                    <div className="w-full flex flex-col items-center gap-1 lg:gap-2">
                      {item.showAddress && (
                        <div className="w-[120px] lg:w-[150px] flex justify-center border rounded">
                          <AddToCalendar
                            event={{
                              name: item.name,
                              details: null,
                              location: item?.address,
                              startsAt: item?.time,
                              endsAt: addOneDay(item?.time),
                            }}
                          >
                            <span className="flex items-center justify-center h-[1.5rem] lg:h-[1.9rem] font-inter text-xs lg:text-base text-[#FCE2B0]">
                              Save Date
                            </span>
                          </AddToCalendar>
                        </div>
                      )}
                      {item.showAddress &&
                        item.hasLiveStream &&
                        liveSchedules && (
                          <DialogModal
                            fillScreen={true}
                            trigger={
                              <div className="w-[120px] lg:w-[150px] flex justify-center">
                                <Button
                                  design="transparent"
                                  type="button"
                                  label="Watch_Live"
                                >
                                  <p className="flex items-center justify-center font-inter text-xs lg:text-base">
                                    Watch Live
                                  </p>
                                </Button>
                              </div>
                            }
                          >
                            <LiveSchedules schedules={liveSchedules} />
                          </DialogModal>
                        )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Image Section - Second on mobile, first on desktop */}
      <div className="flex-1 h-1/2 lg:h-full min-h-[50vh] order-2 lg:order-1 lg:hidden">
        <motion.div
          initial={{ scale: 1 }}
          animate={{
            scale: 1.0,
            transition: {
              delay: 0.3,
              duration: 0.8,
              ease: [0.79, 0.14, 0.15, 0.86],
            },
          }}
          className="relative w-full h-full"
        >
          <Image
            loading="eager"
            layout="fill"
            src={sectionImage || defaultPartsImage}
            alt="event-schedule-image"
            className="object-cover object-center lg:object-top"
            sizes="(max-width: 1024px) 100vw, 50vw"
          />
        </motion.div>
      </div>
    </div>
  ) : (
    <></>
  );
};

export default Parts;
